/**
 * @fileoverview Styles for Custom Link Dialog
 *
 * Styles for the custom link dialog component that replaces
 * MDXEditor's default link dialog. Includes both preview popover
 * and edit modal styles.
 */

/* ============================================================================
   LINK PREVIEW POPOVER
   ============================================================================ */

.wn-link-preview {
  position: fixed;
  z-index: var(--wn-z-dialog);
  display: flex;
  align-items: center;
  gap: var(--wn-space-1);
  padding: var(--wn-space-2);
  background-color: var(--wn-zinc-900);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-lg);
  box-shadow:
    0 10px 15px -3px var(--wn-backdrop-light),
    0 4px 6px -4px var(--wn-overlay-light-10);
  animation: wn-fade-in 0.1s ease-out;
}

@keyframes wn-fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.wn-link-preview-url {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 var(--wn-space-3);
  font-size: var(--wn-font-base);
  font-weight: 500;
  color: var(--wn-brand-400);
  text-decoration: none;
}

.wn-link-preview-url:hover {
  text-decoration: underline;
}

.wn-link-preview-divider {
  width: 1px;
  height: var(--wn-space-5);
  margin: 0 var(--wn-space-1);
  background-color: var(--wn-zinc-700);
}

.wn-link-preview-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--wn-icon-btn-md);
  height: var(--wn-icon-btn-md);
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--wn-radius-sm);
  color: var(--wn-zinc-400);
  cursor: pointer;
  transition:
    background-color var(--wn-transition-fast),
    color var(--wn-transition-fast);
}

.wn-link-preview-btn:hover {
  background-color: var(--wn-overlay-10);
  color: var(--wn-zinc-100);
}

.wn-link-preview-btn--danger:hover {
  background-color: var(--wn-error-alpha-15);
  color: var(--wn-error-400);
}

/* ============================================================================
   LINK DIALOG MODAL
   ============================================================================ */

.wn-link-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--wn-z-dialog);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--wn-backdrop);
  backdrop-filter: blur(2px);
}

.wn-link-dialog {
  width: 100%;
  max-width: 400px;
  margin: var(--wn-space-5);
  background-color: var(--wn-zinc-900);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-lg);
  box-shadow:
    0 20px 25px -5px var(--wn-backdrop-light),
    0 8px 10px -6px var(--wn-overlay-light-10);
}

/* ============================================================================
   DIALOG HEADER
   ============================================================================ */

.wn-link-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--wn-space-5) var(--wn-space-5) var(--wn-space-4);
  border-bottom: 1px solid var(--wn-zinc-800);
}

.wn-link-dialog-title {
  font-size: var(--wn-font-md);
  font-weight: 600;
  color: var(--wn-zinc-100);
  margin: 0;
}

.wn-link-dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--wn-icon-btn-md);
  height: var(--wn-icon-btn-md);
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--wn-radius-sm);
  color: var(--wn-zinc-400);
  cursor: pointer;
  transition:
    background-color var(--wn-transition-fast),
    color var(--wn-transition-fast);
}

.wn-link-dialog-close:hover {
  background-color: var(--wn-zinc-800);
  color: var(--wn-zinc-100);
}

/* ============================================================================
   DIALOG CONTENT
   ============================================================================ */

.wn-link-dialog-content {
  display: flex;
  flex-direction: column;
  gap: var(--wn-space-5);
  padding: var(--wn-space-5);
}

/* ============================================================================
   FORM FIELDS
   ============================================================================ */

.wn-link-dialog-field {
  display: flex;
  flex-direction: column;
  gap: var(--wn-space-2);
}

.wn-link-dialog-label {
  font-size: var(--wn-font-base);
  font-weight: 500;
  color: var(--wn-zinc-300);
}

.wn-link-dialog-input-wrapper {
  position: relative;
}

.wn-link-dialog-input-icon {
  position: absolute;
  top: 50%;
  left: var(--wn-space-4);
  transform: translateY(-50%);
  color: var(--wn-zinc-500);
  pointer-events: none;
}

.wn-link-dialog-input {
  width: 100%;
  padding: var(--wn-space-3) var(--wn-space-4);
  font-size: var(--wn-font-base);
  font-family: inherit;
  background-color: var(--wn-zinc-800);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-md);
  color: var(--wn-zinc-100);
  transition:
    border-color var(--wn-transition-fast),
    box-shadow var(--wn-transition-fast);
}

.wn-link-dialog-input--with-icon {
  padding-left: 2.25rem;
}

.wn-link-dialog-input::placeholder {
  color: var(--wn-zinc-500);
}

.wn-link-dialog-input:focus {
  outline: none;
  border-color: var(--wn-brand-500);
  box-shadow: 0 0 0 2px var(--wn-brand-alpha-20);
}

.wn-link-dialog-input--error {
  border-color: var(--wn-error-500);
}

.wn-link-dialog-input--error:focus {
  border-color: var(--wn-error-500);
  box-shadow: 0 0 0 2px var(--wn-error-alpha-20);
}

.wn-link-dialog-error {
  font-size: var(--wn-font-xs);
  color: var(--wn-error-500);
  margin: 0;
}

/* ============================================================================
   DIALOG FOOTER
   ============================================================================ */

.wn-link-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--wn-space-3);
  padding: var(--wn-space-4) var(--wn-space-5) var(--wn-space-5);
  border-top: 1px solid var(--wn-zinc-800);
}

/* ============================================================================
   LIGHT MODE OVERRIDES
   ============================================================================ */

.wn-light .wn-link-preview {
  background-color: white;
  border-color: var(--wn-zinc-200);
}

.wn-light .wn-link-preview-url {
  color: var(--wn-brand-600);
}

.wn-light .wn-link-preview-divider {
  background-color: var(--wn-zinc-200);
}

.wn-light .wn-link-preview-btn {
  color: var(--wn-zinc-500);
}

.wn-light .wn-link-preview-btn:hover {
  background-color: var(--wn-zinc-100);
  color: var(--wn-zinc-900);
}

.wn-light .wn-link-preview-btn--danger:hover {
  background-color: var(--wn-error-alpha-10);
  color: var(--wn-error-600);
}

.wn-light .wn-link-dialog {
  background-color: white;
  border-color: var(--wn-zinc-200);
}

.wn-light .wn-link-dialog-header {
  border-bottom-color: var(--wn-zinc-200);
}

.wn-light .wn-link-dialog-title {
  color: var(--wn-zinc-900);
}

.wn-light .wn-link-dialog-close {
  color: var(--wn-zinc-500);
}

.wn-light .wn-link-dialog-close:hover {
  background-color: var(--wn-zinc-100);
  color: var(--wn-zinc-900);
}

.wn-light .wn-link-dialog-label {
  color: var(--wn-zinc-700);
}

.wn-light .wn-link-dialog-input-icon {
  color: var(--wn-zinc-400);
}

.wn-light .wn-link-dialog-input {
  background-color: white;
  border-color: var(--wn-zinc-300);
  color: var(--wn-zinc-900);
}

.wn-light .wn-link-dialog-input::placeholder {
  color: var(--wn-zinc-400);
}

.wn-light .wn-link-dialog-footer {
  border-top-color: var(--wn-zinc-200);
}
